import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Plus, X, Clock, CheckCircle, AlertCircle, User, Search,
  Calendar, MapPin, Image, Trash2, Eye, Upload, FileText,
  Wrench, Sparkles, Bed, ClipboardCheck, Package, ToggleLeft, ToggleRight, Phone, Mail, CircleDot,
  MessageSquare, Send, ArrowLeft, Download, ExternalLink, AlertTriangle,
  TreePine, Paintbrush, Lock, Bug, Truck, Shield
} from 'lucide-react';
import { tasksAPI, propertyAPI, bookingAPI, messagesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import CleaningTurnovers from './CleaningTurnovers';

const TASK_TYPES = [
  { id: 'cleaning', label: 'Cleaning', icon: Sparkles, color: 'bg-blue-500' },
  { id: 'linen', label: 'Linen & Laundry', icon: Bed, color: 'bg-purple-500' },
  { id: 'cleaning_linen', label: 'Cleaning + Linen', icon: Package, color: 'bg-indigo-500' },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'bg-amber-500' },
  { id: 'handyman', label: 'Handyman', icon: Wrench, color: 'bg-orange-500' },
  { id: 'inspection', label: 'Inspection', icon: ClipboardCheck, color: 'bg-green-500' },
  { id: 'garden', label: 'Garden & Landscaping', icon: TreePine, color: 'bg-emerald-500' },
  { id: 'decorating', label: 'Decorating', icon: Paintbrush, color: 'bg-pink-500' },
  { id: 'locksmith', label: 'Locksmith', icon: Lock, color: 'bg-yellow-600' },
  { id: 'pest_control', label: 'Pest Control', icon: Bug, color: 'bg-red-500' },
  { id: 'removals', label: 'Removals', icon: Truck, color: 'bg-slate-500' },
  { id: 'security', label: 'Security', icon: Shield, color: 'bg-cyan-600' },
  { id: 'waste', label: 'Waste Removal', icon: Trash2, color: 'bg-stone-500' },
];

const TaskManagement = ({ onNavigateToMessages }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAuthToken } = useAuth();
  const [activeTab, setActiveTab] = useState('tasks');
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [inventoryImpact, setInventoryImpact] = useState(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [reworkTask, setReworkTask] = useState(null);
  const [reworkReason, setReworkReason] = useState('');
  const [reworkSubmitting, setReworkSubmitting] = useState(false);
  
  const [autoAssign, setAutoAssign] = useState(false);
  const [newVendor, setNewVendor] = useState({
    name: '', phone: '', email: '', type: 'cleaning', rating: 5, notes: '', availability: 'available'
  });

  // Conversation-based vendor messaging state
  const [vendorConversations, setVendorConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesEndRef = React.useRef(null);
  const messageInputRef = React.useRef(null);
  
  const [newTask, setNewTask] = useState({
    propertyId: '',
    bookingId: '',
    taskType: 'cleaning',
    title: '',
    description: '',
    scheduledDate: '',
    scheduledTime: '',
    assignee: null,
    priority: 'normal',
    notes: '',
    estimatedCost: ''
  });

  const [invoiceData, setInvoiceData] = useState({
    taskId: '',
    vendorId: '',
    amount: '',
    invoiceNumber: '',
    invoiceDate: '',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const [tasksData, propertiesData, vendorsData, bookingsData] = await Promise.all([
        tasksAPI.getTasks(token, filterStatus !== 'all' ? filterStatus : null, filterType !== 'all' ? filterType : null),
        propertyAPI.getHostProperties(token),
        tasksAPI.getCleaners(token),
        bookingAPI.getHostBookings(token),
      ]);
      setTasks(tasksData);
      setProperties(propertiesData);
      setVendors(vendorsData);
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken, filterStatus, filterType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load host task settings once on mount only — independent of filter
  // changes so toggling auto-assign isn't overwritten when the user
  // changes the filter dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getAuthToken();
        const settings = await tasksAPI.getHostSettings(token);
        if (!cancelled && settings?.autoAssignTasks !== undefined) {
          setAutoAssign(!!settings.autoAssignTasks);
        }
      } catch (err) {
        console.error('Failed to load host task settings:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateTask = async () => {
    if (!newTask.propertyId || !newTask.title || !newTask.scheduledDate) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }

    try {
      const token = getAuthToken();
      await tasksAPI.createTask(newTask, token);
      toast({ title: 'Success', description: 'Task created successfully' });
      setShowCreateModal(false);
      resetNewTask();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create task', variant: 'destructive' });
    }
  };

  const resetNewTask = () => {
    setNewTask({
      propertyId: '',
      bookingId: '',
      taskType: 'cleaning',
      title: '',
      description: '',
      scheduledDate: '',
      scheduledTime: '',
      assignee: null,
      priority: 'normal',
      notes: '',
      estimatedCost: ''
    });
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const token = getAuthToken();
      const result = await tasksAPI.updateTaskStatus(taskId, newStatus, token);
      toast({ title: 'Success', description: `Task ${newStatus === 'completed' ? 'completed' : 'updated'}` });
      fetchData();
      if (selectedTask?._id === taskId) {
        setSelectedTask(prev => ({ ...prev, status: newStatus }));
      }
      // Show inventory impact modal if cleaning/linen task triggered deductions
      if (newStatus === 'completed' && result?.inventoryDeductions?.deductions?.length > 0) {
        setInventoryImpact(result.inventoryDeductions);
        setShowInventoryModal(true);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' });
    }
  };

  const handleRequestRework = async () => {
    if (!reworkTask) return;
    setReworkSubmitting(true);
    try {
      const token = getAuthToken();
      await tasksAPI.requestRework(reworkTask._id, reworkReason.trim(), token);
      toast({ title: 'Rework requested', description: 'The task was reopened and your cleaner has been notified.' });
      setReworkTask(null);
      setReworkReason('');
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error?.response?.data?.detail || 'Could not request rework', variant: 'destructive' });
    } finally {
      setReworkSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const token = getAuthToken();
      await tasksAPI.deleteTask(taskId, token);
      toast({ title: 'Success', description: 'Task deleted' });
      setShowViewModal(false);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete task', variant: 'destructive' });
    }
  };

  const handleAddVendor = async () => {
    if (!newVendor.name || !newVendor.type) {
      toast({ title: 'Error', description: 'Please enter vendor name and type', variant: 'destructive' });
      return;
    }

    try {
      const token = getAuthToken();
      const vendor = await tasksAPI.addCleaner(newVendor, token);
      setVendors(prev => [...prev, vendor]);
      setNewVendor({ name: '', phone: '', email: '', type: 'cleaning', rating: 5, notes: '' });
      setShowVendorModal(false);
      toast({ title: 'Success', description: 'Vendor added' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add vendor', variant: 'destructive' });
    }
  };

  const handleUpdateVendorAvailability = async (vendorId, availability) => {
    try {
      const token = getAuthToken();
      const updated = await tasksAPI.updateVendor(vendorId, { availability }, token);
      setVendors(prev => prev.map(v => v._id === vendorId ? { ...v, ...updated } : v));
      toast({ title: 'Updated', description: `Vendor set to ${availability}` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update vendor', variant: 'destructive' });
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Remove this vendor?')) return;
    try {
      const token = getAuthToken();
      await tasksAPI.deleteVendor(vendorId, token);
      setVendors(prev => prev.filter(v => v._id !== vendorId));
      toast({ title: 'Removed', description: 'Vendor removed' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove vendor', variant: 'destructive' });
    }
  };

  // --- Vendor Messaging (conversation-based) ---
  const fetchVendorConversations = useCallback(async () => {
    try {
      const token = getAuthToken();
      // Fetch all conversations, filter to task/provider type
      const allConversations = await messagesAPI.getConversations(token);
      const taskConvos = allConversations.filter(c =>
        c.conversationType === 'task' || c.participantType === 'provider'
      );
      setVendorConversations(taskConvos);
    } catch (err) {
      console.error('Failed to fetch vendor conversations:', err);
    }
  }, [getAuthToken]);

  const openConversation = async (conversation) => {
    setActiveConversation(conversation);
    setMessagesLoading(true);
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getConversation(conversation._id, token);
      setConversationMessages(data.messages || []);
      // Mark as read
      await messagesAPI.markConversationRead(conversation._id, token).catch(() => {});
      fetchVendorConversations();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load messages', variant: 'destructive' });
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    try {
      const token = getAuthToken();
      const msg = await messagesAPI.sendMessage({
        conversationId: activeConversation._id,
        content: newMessage.trim()
      }, token);
      setConversationMessages(prev => [...prev, msg]);
      setNewMessage('');
      fetchVendorConversations();
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        messageInputRef.current?.focus();
      }, 100);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    }
  };

  // Start a new conversation with a provider from a task
  const startConversationWithProvider = async (task) => {
    if (!task.assignedProviderId) {
      toast({ title: 'No Provider', description: 'This task has no assigned provider to message.', variant: 'destructive' });
      return;
    }
    try {
      const token = getAuthToken();
      const conv = await messagesAPI.createConversation({
        participantId: task.assignedProviderId,
        conversationType: 'task',
        participantType: 'provider',
        relatedType: 'task',
        relatedId: task._id,
        initialMessage: ''
      }, token);
      setActiveTab('messages');
      await fetchVendorConversations();
      // Open the newly created conversation
      const data = await messagesAPI.getConversation(conv._id, token);
      setActiveConversation({ ...conv, guestName: task.assignee?.name || task.assignedProviderName || 'Provider' });
      setConversationMessages(data.messages || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to start conversation', variant: 'destructive' });
    }
  };

  // Fetch conversations when Messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchVendorConversations();
    }
  }, [activeTab, fetchVendorConversations]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (conversationMessages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages]);

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available': return 'bg-green-500';
      case 'busy': return 'bg-amber-500';
      case 'unavailable': return 'bg-red-500';
      default: return 'bg-green-500';
    }
  };

  const getAvailabilityBadge = (availability) => {
    switch (availability) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'busy': return 'bg-amber-100 text-amber-700';
      case 'unavailable': return 'bg-red-100 text-red-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'low': return 'bg-gray-100 text-gray-600';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getTaskTypeInfo = (typeId) => {
    return TASK_TYPES.find(t => t.id === typeId) || TASK_TYPES[0];
  };

  const taskStats = {
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    total: tasks.length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-[#1F3A68]">Services & Operations</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/services')}>
            <Search size={16} className="mr-2" />
            Find Vendors
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-[#1F3A68]">
            <Plus size={16} className="mr-2" />
            Create Task
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white">
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
          <TabsTrigger value="turnovers">Turnovers</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-1.5">
            <MessageSquare size={14} />
            Messages
            {vendorConversations.reduce((sum, t) => sum + (t.unreadCount || 0), 0) > 0 && (
              <span className="bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                {vendorConversations.reduce((sum, t) => sum + (t.unreadCount || 0), 0)}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="turnovers" className="space-y-6">
          <CleaningTurnovers />
        </TabsContent>

        <TabsContent value="tasks" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock size={24} className="text-amber-600" />
                <div>
                  <div className="text-2xl font-bold text-amber-700">{taskStats.pending}</div>
                  <div className="text-sm text-amber-600">Pending</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertCircle size={24} className="text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-700">{taskStats.inProgress}</div>
                  <div className="text-sm text-blue-600">In Progress</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle size={24} className="text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-700">{taskStats.completed}</div>
                  <div className="text-sm text-green-600">Completed</div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-4 flex items-center gap-3">
                <FileText size={24} className="text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-gray-700">{taskStats.total}</div>
                  <div className="text-sm text-gray-600">Total Tasks</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <div className="flex gap-1">
              {['all', 'pending', 'in_progress', 'completed'].map(status => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  onClick={() => setFilterStatus(status)}
                  className={filterStatus === status ? 'bg-[#1F3A68]' : ''}
                  size="sm"
                >
                  {status === 'all' ? 'All' : status === 'in_progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
            <div className="border-l pl-2 flex flex-wrap gap-1">
              {['all', ...TASK_TYPES.map(t => t.id)].map(type => (
                <Button
                  key={type}
                  variant={filterType === type ? 'default' : 'outline'}
                  onClick={() => setFilterType(type)}
                  className={filterType === type ? 'bg-[#1F3A68]' : ''}
                  size="sm"
                >
                  {type === 'all' ? 'All Types' : getTaskTypeInfo(type).label}
                </Button>
              ))}
            </div>
          </div>

          {/* Task List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A68] mx-auto"></div>
            </div>
          ) : tasks.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-gray-700 mb-2">No Tasks</h3>
                <p className="text-gray-600 mb-4">Create a task to manage services</p>
                <Button onClick={() => setShowCreateModal(true)} className="bg-[#1F3A68]">
                  <Plus size={16} className="mr-2" />
                  Create Task
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tasks.map(task => {
                const typeInfo = getTaskTypeInfo(task.taskType);
                const TypeIcon = typeInfo.icon;
                return (
                  <Card
                    key={task._id}
                    className="hover:shadow-md transition-shadow cursor-pointer border hover:border-[#1F3A68]/30"
                    onClick={() => { setSelectedTask(task); setShowViewModal(true); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className={`w-10 h-10 ${typeInfo.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <TypeIcon size={20} className="text-white" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                                {task.status === 'in_progress' ? 'In Progress' : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                              {task.source === 'auto_dispatch' && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                  Auto-dispatched
                                </span>
                              )}
                              {task.linenLockEnabled && !task.linenVerified && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                                  Linen Lock Active
                                </span>
                              )}
                              {task.linenLockEnabled && task.linenVerified && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                  Linen Verified
                                </span>
                              )}
                              {task.status === 'completed' && (task.evidencePhotos?.length > 0 || task.completionPhotos?.length > 0) && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 flex items-center gap-1">
                                  <Image size={10} /> {task.evidencePhotos?.length || task.completionPhotos?.length} Photos
                                </span>
                              )}
                            </div>
                            <h4 className="font-bold text-gray-900">{task.title}</h4>
                            {task.description && (
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{task.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1 flex-wrap">
                              <span className="flex items-center gap-1">
                                <MapPin size={14} />
                                {task.propertyTitle}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {task.scheduledDate} {task.scheduledTime}
                              </span>
                              {task.assignee?.name && (
                                <span className="flex items-center gap-1">
                                  <User size={14} />
                                  {task.assignee.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {task.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); handleUpdateStatus(task._id, task.status === 'pending' ? 'in_progress' : 'completed'); }}
                            >
                              {task.status === 'pending' ? 'Start' : 'Complete'}
                            </Button>
                          )}
                          {task.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-700 border-amber-300 hover:bg-amber-50"
                              onClick={(e) => { e.stopPropagation(); setReworkReason(''); setReworkTask(task); }}
                            >
                              Not satisfied
                            </Button>
                          )}
                          {task.reworkRequired && task.status !== 'completed' && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                              Rework
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); setSelectedTask(task); setShowViewModal(true); }}
                          >
                            <Eye size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="vendors" className="space-y-6">
          {/* Auto-Assign Toggle */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-[#1F3A68] flex items-center gap-2">
                    <Sparkles size={16} />
                    Auto-Assign Tasks
                  </h4>
                  <p className="text-sm text-gray-600 mt-0.5">
                    When a guest checks out, automatically offer the turnover clean to your cleaner. They accept or decline by email; if they can't make it, it passes to your backup. Uses each property's preferred cleaner, or your first cleaner if none is set.
                  </p>
                </div>
                <button
                  onClick={async () => {
                    // Use functional update so rapid clicks compute against
                    // the latest state, not a stale closure.
                    let intent;
                    setAutoAssign((prev) => {
                      intent = !prev;
                      return intent;
                    });
                    try {
                      const token = getAuthToken();
                      const result = await tasksAPI.updateHostSettings({ autoAssignTasks: intent }, token);
                      // Sync from server response so the toggle reflects the
                      // persisted value, not just the optimistic guess.
                      if (result?.autoAssignTasks !== undefined) {
                        setAutoAssign(!!result.autoAssignTasks);
                      }
                      toast({ title: intent ? 'Auto-assign enabled' : 'Auto-assign disabled' });
                    } catch (err) {
                      console.error('Failed to update auto-assign:', err);
                      setAutoAssign(!intent);
                      toast({
                        title: 'Error',
                        description: err?.response?.data?.detail || 'Failed to save setting',
                        variant: 'destructive',
                      });
                    }
                  }}
                  className="flex-shrink-0 ml-4"
                  title={autoAssign ? 'Disable auto-assign' : 'Enable auto-assign'}
                >
                  {autoAssign
                    ? <ToggleRight size={36} className="text-emerald-600" />
                    : <ToggleLeft size={36} className="text-gray-400" />
                  }
                </button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Vendor Management</CardTitle>
                <Button onClick={() => navigate('/services')} className="bg-[#1F3A68]">
                  <Search size={16} className="mr-2" />
                  Find Vendors on Marketplace
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {vendors.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Search size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No vendors yet</p>
                  <p className="text-sm mt-1">Browse the marketplace to find and hire verified service providers</p>
                  <Button variant="outline" className="mt-4 border-[#1F3A68] text-[#1F3A68]" onClick={() => navigate('/services')}>
                    <ExternalLink size={14} className="mr-2" /> Browse Marketplace
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vendors.map(vendor => {
                    const typeInfo = getTaskTypeInfo(vendor.type);
                    const TypeIcon = typeInfo?.icon || User;
                    const availability = vendor.availability || 'available';
                    return (
                      <Card key={vendor._id} className="relative overflow-hidden">
                        {/* Availability color bar */}
                        <div className={`absolute top-0 left-0 right-0 h-1 ${getAvailabilityColor(availability)}`} />
                        <CardContent className="p-4 pt-5">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 ${typeInfo?.color || 'bg-[#1F3A68]'} rounded-full flex items-center justify-center text-white relative`}>
                              <TypeIcon size={20} />
                              {/* Availability dot */}
                              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${getAvailabilityColor(availability)}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold truncate">{vendor.name}</h4>
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold capitalize ${getAvailabilityBadge(availability)}`}>
                                  {availability}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 capitalize">{vendor.type?.replace('_', ' ') || 'General'}</p>
                              {vendor.phone && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                  <Phone size={10} /> {vendor.phone}
                                </p>
                              )}
                              {vendor.email && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Mail size={10} /> {vendor.email}
                                </p>
                              )}
                              {vendor.notes && (
                                <p className="text-xs text-gray-400 mt-1 truncate">{vendor.notes}</p>
                              )}
                            </div>
                          </div>
                          {/* Availability quick-toggle */}
                          <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                            {['available', 'busy', 'unavailable'].map(status => (
                              <button
                                key={status}
                                onClick={() => handleUpdateVendorAvailability(vendor._id, status)}
                                className={`flex-1 text-[10px] py-1 rounded font-medium transition-colors ${
                                  availability === status
                                    ? status === 'available' ? 'bg-green-500 text-white'
                                    : status === 'busy' ? 'bg-amber-500 text-white'
                                    : 'bg-red-500 text-white'
                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                              >
                                {status === 'available' ? 'Available' : status === 'busy' ? 'Busy' : 'Unavailable'}
                              </button>
                            ))}
                            <button
                              onClick={() => handleDeleteVendor(vendor._id)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors ml-1"
                              title="Remove vendor"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          {!activeConversation ? (
            /* Conversation List View */
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare size={20} />
                    Vendor Messages
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {vendorConversations.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No vendor conversations yet</p>
                    <p className="text-xs text-gray-400 mt-1">Click "Message" on a task with an assigned vendor to start a conversation</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {vendorConversations.map(conv => {
                      const providerName = conv.guestName || conv.participantName || 'Provider';
                      const lastMsg = conv.lastMessage;
                      const unread = conv.unreadHost || 0;
                      return (
                        <button
                          key={conv._id}
                          onClick={() => openConversation(conv)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border"
                        >
                          <div className="w-10 h-10 bg-[#1F3A68] rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                            {providerName?.charAt(0)?.toUpperCase() || 'V'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-sm truncate">{providerName}</h4>
                              {lastMsg?.createdAt && (
                                <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                                  {new Date(lastMsg.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500 truncate">
                                {lastMsg ? (
                                  <>{lastMsg.senderRole === 'host' ? 'You: ' : ''}{lastMsg.content || lastMsg.text}</>
                                ) : (
                                  <span className="italic text-gray-400">No messages yet — tap to start</span>
                                )}
                              </p>
                              {unread > 0 && (
                                <span className="bg-red-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                                  {unread}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-400 capitalize">
                              {conv.conversationType === 'task' ? 'Task conversation' : 'Service provider'}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Active Conversation View */
            <Card className="flex flex-col" style={{ height: '500px' }}>
              {/* Conversation Header */}
              <div className="flex items-center gap-3 p-4 border-b bg-gray-50 rounded-t-lg">
                <Button variant="ghost" size="icon" onClick={() => { setActiveConversation(null); setConversationMessages([]); }}>
                  <ArrowLeft size={20} />
                </Button>
                <div className="w-8 h-8 bg-[#1F3A68] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {(activeConversation.guestName || activeConversation.participantName || 'P')?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{activeConversation.guestName || activeConversation.participantName || 'Provider'}</h4>
                  <p className="text-[10px] text-gray-500">Service Provider</p>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messagesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1F3A68]"></div>
                  </div>
                ) : conversationMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    <div className="text-center">
                      <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
                      <p>Start a conversation</p>
                      <p className="text-xs mt-1">Messages about tasks, schedules, and operations</p>
                    </div>
                  </div>
                ) : (
                  conversationMessages.map(msg => (
                    <div
                      key={msg._id}
                      className={`flex ${msg.senderRole === 'host' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        msg.senderRole === 'host'
                          ? 'bg-[#1F3A68] text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                      }`}>
                        {msg.photoUrl && (
                          <img src={msg.photoUrl} alt="Photo" className="w-full rounded-lg mb-1 max-h-48 object-cover cursor-pointer" onClick={() => window.open(msg.photoUrl, '_blank')} />
                        )}
                        <p className="text-sm">{msg.content || msg.text}</p>
                        <p className={`text-[10px] mt-0.5 ${msg.senderRole === 'host' ? 'text-blue-200' : 'text-gray-400'}`}>
                          {new Date(msg.createdAt || msg.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-3 border-t bg-white rounded-b-lg">
                <div className="flex gap-2">
                  <Input
                    ref={messageInputRef}
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendMessage(); } }}
                    className="flex-1"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                    className="bg-[#1F3A68]"
                    size="icon"
                  >
                    <Send size={18} />
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Create New Task</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Property *</Label>
                <select
                  value={newTask.propertyId}
                  onChange={(e) => setNewTask(prev => ({ ...prev, propertyId: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 border rounded-lg"
                >
                  <option value="">Select property</option>
                  {properties.map(p => (
                    <option key={p._id} value={p._id}>{p.title}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Link to Booking (Optional)</Label>
                <select
                  value={newTask.bookingId}
                  onChange={(e) => setNewTask(prev => ({ ...prev, bookingId: e.target.value }))}
                  className="w-full mt-1 h-10 px-3 border rounded-lg"
                >
                  <option value="">No booking linked</option>
                  {bookings.filter(b => !newTask.propertyId || b.propertyId === newTask.propertyId).map(b => (
                    <option key={b._id} value={b._id}>
                      {b.guestName} - {b.checkIn} to {b.checkOut}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Service Type *</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1 max-h-60 overflow-y-auto">
                  {TASK_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setNewTask(prev => ({ ...prev, taskType: type.id }))}
                        className={`p-2.5 rounded-lg border text-center transition-colors ${
                          newTask.taskType === type.id
                            ? 'border-[#1F3A68] bg-[#1F3A68]/10'
                            : 'hover:border-gray-300'
                        }`}
                      >
                        <Icon size={18} className={`mx-auto mb-1 ${newTask.taskType === type.id ? 'text-[#1F3A68]' : 'text-gray-500'}`} />
                        <span className="text-xs leading-tight block">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full mt-1 h-10 px-3 border rounded-lg"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <Label>Estimated Cost (£)</Label>
                  <Input
                    type="number"
                    value={newTask.estimatedCost}
                    onChange={(e) => setNewTask(prev => ({ ...prev, estimatedCost: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <Label>Title *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Checkout Cleaning"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Task details..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newTask.scheduledDate}
                    onChange={(e) => setNewTask(prev => ({ ...prev, scheduledDate: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={newTask.scheduledTime}
                    onChange={(e) => setNewTask(prev => ({ ...prev, scheduledTime: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label>Assign Vendor</Label>
                <select
                  value={newTask.assignee?._id || ''}
                  onChange={(e) => {
                    if (e.target.value === '__auto__') {
                      setNewTask(prev => ({ ...prev, assignee: null, autoMatch: true }));
                    } else {
                      const vendor = vendors.find(v => v._id === e.target.value);
                      setNewTask(prev => ({ ...prev, assignee: vendor || null, autoMatch: false }));
                    }
                  }}
                  className="w-full mt-1 h-10 px-3 border rounded-lg"
                >
                  <option value="">Unassigned</option>
                  <option value="__auto__">🔍 Auto-match to available vendor</option>
                  {vendors.map(v => (
                    <option key={v._id} value={v._id} disabled={v.availability === 'unavailable'}>
                      {v.name} ({v.type || 'General'}) — {v.availability === 'available' ? '✓ Available' : v.availability === 'busy' ? '⏳ Busy' : '✗ Unavailable'}
                    </option>
                  ))}
                </select>
                {newTask.autoMatch && (
                  <p className="text-xs text-blue-600 mt-1">The platform will automatically match this task to the best available vendor for this service type.</p>
                )}
                {!newTask.assignee && !newTask.autoMatch && vendors.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    No vendors in your list yet.{' '}
                    <button type="button" onClick={() => navigate('/services')} className="text-[#1F3A68] underline">Find vendors on the marketplace</button>
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCreateTask} className="flex-1 bg-[#1F3A68]">
                  Create Task
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Task Modal — Detailed */}
      {/* Inventory Impact Modal */}
      {reworkTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-3 pb-3 border-b">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1F3A68]">Not satisfied?</h3>
                  <p className="text-sm text-gray-500">Reopen this job and ask for a redo. {reworkTask.assignedCleanerName || reworkTask.assignee?.name ? 'Your cleaner will be notified.' : ''}</p>
                </div>
                <button onClick={() => setReworkTask(null)} className="ml-auto text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">{reworkTask.title}</p>
                <label className="block text-sm text-gray-600 mb-1">What needs fixing? (optional)</label>
                <Textarea
                  value={reworkReason}
                  onChange={(e) => setReworkReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. bathroom wasn't cleaned, bins not emptied"
                />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setReworkTask(null)}>Cancel</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={reworkSubmitting}
                  onClick={handleRequestRework}
                >
                  {reworkSubmitting ? 'Sending…' : 'Request redo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showInventoryModal && inventoryImpact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={22} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#1F3A68]">Inventory Updated</h3>
                  <p className="text-sm text-gray-500">
                    {inventoryImpact.taskType === 'cleaning' ? 'Cleaning' : inventoryImpact.taskType === 'linen' ? 'Linen' : 'Cleaning & Linen'} task completed — {inventoryImpact.totalItemsDeducted} item{inventoryImpact.totalItemsDeducted !== 1 ? 's' : ''} deducted
                  </p>
                </div>
                <button onClick={() => setShowInventoryModal(false)} className="ml-auto text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              {/* Deductions Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Item</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Category</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-600">Used</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-600">Left</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryImpact.deductions.map((d, i) => (
                      <tr key={i} className={`border-t ${d.remaining <= 0 ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 font-medium">{d.name}</td>
                        <td className="px-3 py-2 text-gray-500 capitalize">{d.category}</td>
                        <td className="px-3 py-2 text-center text-red-600">-{d.deducted} {d.unit}</td>
                        <td className={`px-3 py-2 text-center font-semibold ${d.remaining <= 0 ? 'text-red-600' : 'text-gray-700'}`}>{d.remaining}</td>
                        <td className="px-3 py-2 text-right text-gray-500">£{d.cost?.toFixed(2) || '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t">
                    <tr>
                      <td colSpan={4} className="px-3 py-2 text-right font-semibold text-gray-700">Total consumable cost:</td>
                      <td className="px-3 py-2 text-right font-bold text-[#1F3A68]">£{inventoryImpact.totalCostImpact?.toFixed(2) || '0.00'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Low Stock Alerts */}
              {inventoryImpact.lowStockAlerts?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={18} className="text-amber-600" />
                    <span className="font-semibold text-amber-800">Low Stock Alert</span>
                  </div>
                  <ul className="space-y-1">
                    {inventoryImpact.lowStockAlerts.map((a, i) => (
                      <li key={i} className="text-sm text-amber-700 flex justify-between">
                        <span>{a.itemName}</span>
                        <span className="font-medium">{a.currentStock} / {a.minimumStock} {a.unit} remaining</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 text-amber-700 border-amber-300 hover:bg-amber-100"
                    onClick={() => { setShowInventoryModal(false); setActiveTab('inventory'); }}
                  >
                    View Inventory
                  </Button>
                </div>
              )}

              {/* Dismiss */}
              <div className="flex justify-end pt-2">
                <Button onClick={() => setShowInventoryModal(false)} className="bg-[#1F3A68]">
                  Done
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showViewModal && selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
              <div>
                <CardTitle className="text-xl">{selectedTask.title}</CardTitle>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedTask.status)}`}>
                    {selectedTask.status === 'in_progress' ? 'In Progress' : selectedTask.status.charAt(0).toUpperCase() + selectedTask.status.slice(1)}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getPriorityColor(selectedTask.priority)}`}>
                    {selectedTask.priority} priority
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getTaskTypeInfo(selectedTask.taskType).color} text-white`}>
                    {getTaskTypeInfo(selectedTask.taskType).label}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent className="space-y-5 pt-5">

              {/* Job Details + Property Info side by side */}
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Job Details */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-[#1F3A68] text-sm uppercase tracking-wide">Job Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Service Type:</span>
                      <span className="font-medium capitalize">{(selectedTask.taskType || '').replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Scheduled:</span>
                      <span className="font-medium">{selectedTask.scheduledDate} {selectedTask.scheduledTime || ''}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Priority:</span>
                      <span className="font-medium capitalize">{selectedTask.priority}</span>
                    </div>
                    {(selectedTask.estimatedCost || selectedTask.amount || selectedTask.grossAmount) && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-500">{selectedTask.status === 'completed' ? 'Charged:' : 'Est. Cost:'}</span>
                          <span className="font-semibold text-[#1F3A68]">
                            £{Number(selectedTask.grossAmount || selectedTask.amount || selectedTask.estimatedCost || 0).toFixed(2)}
                          </span>
                        </div>
                        {selectedTask.status === 'completed' && selectedTask.platformFee > 0 && (
                          <div className="flex justify-between text-xs text-gray-400">
                            <span>Platform fee (5%):</span>
                            <span>£{Number(selectedTask.platformFee).toFixed(2)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {selectedTask.source === 'auto_dispatch' && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Source:</span>
                        <span className="text-blue-600 font-medium text-xs">Auto-dispatched</span>
                      </div>
                    )}
                    {selectedTask.createdAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Created:</span>
                        <span className="font-medium">{new Date(selectedTask.createdAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Property Info */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-[#1F3A68] text-sm uppercase tracking-wide">Property</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Name:</span>
                      <p className="font-medium">{selectedTask.propertyTitle}</p>
                    </div>
                    {selectedTask.propertyAddress && (
                      <div>
                        <span className="text-gray-500">Address:</span>
                        <p className="font-medium">{selectedTask.propertyAddress}</p>
                      </div>
                    )}
                    {selectedTask.bookingRef && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Booking Ref:</span>
                        <span className="font-mono font-medium">{selectedTask.bookingRef}</span>
                      </div>
                    )}
                    {selectedTask.guestName && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Guest:</span>
                        <span className="font-medium">{selectedTask.guestName}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedTask.description && (
                <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-100">
                  <h4 className="font-semibold text-[#1F3A68] text-sm uppercase tracking-wide mb-2">Description & Instructions</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTask.description}</p>
                </div>
              )}

              {/* Assigned Vendor */}
              {selectedTask.assignee?.name && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#1F3A68] text-sm uppercase tracking-wide mb-3">Assigned Vendor</h4>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1F3A68] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {selectedTask.assignee.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{selectedTask.assignee.name}</p>
                      {selectedTask.assignee.phone && (
                        <p className="text-sm text-gray-500 flex items-center gap-1"><Phone size={12} /> {selectedTask.assignee.phone}</p>
                      )}
                      {selectedTask.assignee.email && (
                        <p className="text-sm text-gray-500 flex items-center gap-1"><Mail size={12} /> {selectedTask.assignee.email}</p>
                      )}
                    </div>
                    {selectedTask.assignee.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#1F3A68] text-[#1F3A68] flex-shrink-0"
                        onClick={async () => {
                          try {
                            const token = getAuthToken();
                            const conv = await messagesAPI.createConversation({
                              participantId: selectedTask.assignee.id,
                              conversationType: 'task',
                              participantType: 'provider',
                              relatedType: 'task',
                              relatedId: selectedTask._id,
                              initialMessage: `Hi, regarding the ${selectedTask.taskType} task for ${selectedTask.propertyTitle || 'your property'}.`
                            }, token);
                            setShowViewModal(false);
                            setSelectedTask(null);
                            // Open the conversation in the Messages tab
                            const data = await messagesAPI.getConversation(conv._id, token);
                            setActiveConversation({ ...conv, guestName: selectedTask.assignee?.name || 'Provider' });
                            setConversationMessages(data.messages || []);
                            setActiveTab('messages');
                            fetchVendorConversations();
                          } catch (err) {
                            toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to start conversation', variant: 'destructive' });
                          }
                        }}
                      >
                        <MessageSquare size={14} className="mr-1" /> Message
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Evidence Photos Section */}
              {(selectedTask.evidencePhotos?.length > 0 || selectedTask.completionPhotos?.length > 0 || selectedTask.completionImages?.length > 0) ? (
                <div>
                  <h4 className="font-semibold text-[#1F3A68] text-sm uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Image size={14} /> Evidence Photos ({selectedTask.evidencePhotos?.length || selectedTask.completionPhotos?.length || selectedTask.completionImages?.length || 0})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {(selectedTask.evidencePhotos || selectedTask.completionPhotos || []).map((photo, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden border bg-gray-50 shadow-sm">
                        <img
                          src={photo.url || photo}
                          alt={photo.label || `Evidence ${i + 1}`}
                          className="w-full h-32 object-cover cursor-pointer"
                          onClick={() => window.open(photo.url || photo, '_blank')}
                        />
                        <div className="absolute top-1 left-1 flex flex-wrap gap-1">
                          {photo.label && (
                            <span className="text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">{photo.label}</span>
                          )}
                          {photo.isLiveCapture && (
                            <span className="text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded font-medium">LIVE</span>
                          )}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <p className="text-[10px] text-white/80">
                            {photo.timestamp ? new Date(photo.timestamp).toLocaleString() : ''}
                          </p>
                          {photo.capturedBy && (
                            <p className="text-[10px] text-white/60">By: {photo.capturedBy}</p>
                          )}
                        </div>
                        <a
                          href={photo.url || photo}
                          download={`evidence-${selectedTask._id}-${i + 1}.jpg`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute top-1 right-1 p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
                          onClick={e => e.stopPropagation()}
                        >
                          <Download size={12} className="text-[#1F3A68]" />
                        </a>
                      </div>
                    ))}
                    {/* Legacy completionImages (URL only) */}
                    {!selectedTask.evidencePhotos?.length && selectedTask.completionImages?.map((img, i) => (
                      <div key={`legacy-${i}`} className="relative group rounded-lg overflow-hidden border">
                        <img src={img} alt={`Completion ${i + 1}`} className="w-full h-32 object-cover cursor-pointer" onClick={() => window.open(img, '_blank')} />
                        <a href={img} download={`completion-${i + 1}.jpg`} target="_blank" rel="noopener noreferrer"
                          className="absolute top-1 right-1 p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
                          onClick={e => e.stopPropagation()}>
                          <Download size={12} className="text-[#1F3A68]" />
                        </a>
                      </div>
                    ))}
                  </div>
                  {selectedTask.status === 'in_progress' && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-sm text-blue-700 font-medium">Review the provider's evidence photos before confirming task completion.</p>
                    </div>
                  )}
                </div>
              ) : selectedTask.status === 'in_progress' ? (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">No evidence photos yet</p>
                      <p className="text-xs text-amber-600 mt-1">The provider has not submitted any photos. Evidence photos are required before task completion.</p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Completion Info */}
              {selectedTask.completedAt && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="font-semibold text-green-800 text-sm">Task Completed</span>
                  </div>
                  <p className="text-green-700 text-sm">
                    {new Date(selectedTask.completedAt).toLocaleString()}
                  </p>
                  {selectedTask.completionNotes && (
                    <p className="text-green-600 text-sm mt-2 italic">"{selectedTask.completionNotes}"</p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2 border-t">
                {selectedTask.status !== 'completed' && (
                  <>
                    {selectedTask.status === 'pending' && (
                      <Button
                        onClick={() => handleUpdateStatus(selectedTask._id, 'in_progress')}
                        className="flex-1 bg-blue-600"
                      >
                        Start Task
                      </Button>
                    )}
                    {selectedTask.status === 'in_progress' && (
                      <Button
                        onClick={() => {
                          if (!selectedTask.evidencePhotos?.length && !selectedTask.completionPhotos?.length) {
                            toast({ title: 'Photos Required', description: 'The provider must submit evidence photos before you can mark this task as complete.', variant: 'destructive' });
                            return;
                          }
                          handleUpdateStatus(selectedTask._id, 'completed');
                        }}
                        className="flex-1 bg-green-600"
                      >
                        <CheckCircle size={16} className="mr-2" /> Confirm Complete
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowViewModal(false)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDeleteTask(selectedTask._id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default TaskManagement;
